import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ChevronDown, LogOut, Settings, Users } from 'lucide-react';
import { cn } from '../lib/cn';
import { Menu, MenuItem, MenuSeparator } from './Menu';

export type ProfileMenuUser = {
  name: string;
  email?: string;
};

export type ProfileMenuLabels = {
  settings?: string;
  switchAccount?: string;
  signOut?: string;
};

export type ProfileMenuProps = {
  user: ProfileMenuUser;
  onSettings?: () => void;
  onSwitchAccount?: () => void;
  onSignOut?: () => void;
  /** Extra items before sign out */
  extraItems?: ReactNode;
  align?: 'start' | 'end';
  /**
   * Preferred vertical placement. `auto` flips up when there isn’t room below
   * (e.g. sidebar footer).
   */
  side?: 'top' | 'bottom' | 'auto';
  className?: string;
  /** Show user name on the trigger (desktop). When false, trigger is name-less chevron only. */
  showName?: boolean;
  /** Optional i18n overrides for built-in menu items. */
  labels?: ProfileMenuLabels;
};

type Placement = {
  side: 'top' | 'bottom';
  align: 'start' | 'end';
};

/**
 * Account overflow — soft brand when open; solid menu panel.
 * Auto-flips above the trigger when near the bottom of the viewport (sidebar footer).
 */
export function ProfileMenu({
  user,
  onSettings,
  onSwitchAccount,
  onSignOut,
  extraItems,
  align = 'end',
  side = 'auto',
  className,
  showName = true,
  labels,
}: ProfileMenuProps) {
  const settingsLabel = labels?.settings ?? 'Settings';
  const switchAccountLabel = labels?.switchAccount ?? 'Switch account';
  const signOutLabel = labels?.signOut ?? 'Sign out';
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement>({
    side: side === 'top' ? 'top' : 'bottom',
    align,
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node) || !rootRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;

    const trigger = rootRef.current.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuH = menuEl?.offsetHeight ?? 220;
    const menuW = menuEl?.offsetWidth ?? 220;
    const gap = 6;
    const pad = 8;

    let nextSide: 'top' | 'bottom' =
      side === 'auto' ? 'bottom' : side === 'top' ? 'top' : 'bottom';

    if (side === 'auto') {
      const spaceBelow = window.innerHeight - trigger.bottom - pad;
      const spaceAbove = trigger.top - pad;
      if (spaceBelow < menuH + gap && spaceAbove > spaceBelow) {
        nextSide = 'top';
      }
    }

    // Prefer opening toward the larger horizontal space so a narrow sidebar
    // doesn't clip the 220px panel off the left edge of the viewport.
    let nextAlign: 'start' | 'end' = align;
    if (align === 'end') {
      const overflowsLeft = trigger.right - menuW < pad;
      const roomRight = window.innerWidth - trigger.left - pad >= menuW;
      if (overflowsLeft && roomRight) nextAlign = 'start';
    } else {
      const overflowsRight = trigger.left + menuW > window.innerWidth - pad;
      const roomLeft = trigger.right - menuW >= pad;
      if (overflowsRight && roomLeft) nextAlign = 'end';
    }

    setPlacement({ side: nextSide, align: nextAlign });
  }, [open, side, align]);

  return (
    <div ref={rootRef} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={showName ? undefined : `Account menu for ${user.name}`}
        onClick={() => setOpen(o => !o)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-ds-md px-2 py-1 text-ds-sm font-medium',
          'text-ds-text-secondary transition-colors duration-ds-fast',
          'hover:bg-ds-subtle hover:text-ds-text',
          open && 'bg-ds-brand-subtle text-ds-brand',
        )}
      >
        {showName ? (
          <span className="max-w-[8rem] truncate">{user.name}</span>
        ) : null}
        <ChevronDown
          className={cn(
            'size-3.5 shrink-0 opacity-60 transition-transform duration-ds-fast',
            open && placement.side === 'top' && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      {open ? (
        <div
          id={menuId}
          ref={menuRef}
          className={cn(
            // Above AppShell top bar chrome / sticky entity headers.
            'absolute z-[60]',
            placement.side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
            placement.align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          <Menu width={220}>
            <div className="border-b border-ds-border px-2.5 py-2">
              <div className="truncate text-ds-sm font-medium text-ds-text">{user.name}</div>
              {user.email ? (
                <div className="truncate text-ds-xs text-ds-muted">{user.email}</div>
              ) : null}
            </div>
            {onSettings ? (
              <MenuItem
                icon={<Settings />}
                onClick={() => {
                  setOpen(false);
                  onSettings();
                }}
              >
                {settingsLabel}
              </MenuItem>
            ) : null}
            {onSwitchAccount ? (
              <MenuItem
                icon={<Users />}
                onClick={() => {
                  setOpen(false);
                  onSwitchAccount();
                }}
              >
                {switchAccountLabel}
              </MenuItem>
            ) : null}
            {extraItems}
            {onSignOut ? (
              <>
                <MenuSeparator />
                <MenuItem
                  icon={<LogOut />}
                  destructive
                  onClick={() => {
                    setOpen(false);
                    onSignOut();
                  }}
                >
                  {signOutLabel}
                </MenuItem>
              </>
            ) : null}
          </Menu>
        </div>
      ) : null}
    </div>
  );
}
